// src/pages/admin/CompanyManagementPage.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tooltip,
  Snackbar,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
} from '@mui/icons-material';

// Composants
import CompanyForm from '../../components/admin/CompanyForm';

// Actions Redux (à implémenter)
import {
  fetchCompanies,
  deleteCompany,
  createCompany,
  updateCompany,
  toggleCompanyStatus,
} from '../../features/companies/companiesSlice';

const CompanyManagementPage = () => {
  const dispatch = useDispatch();
  const { companies, isLoading, error, meta } = useSelector((state) => state.companies);

  // États locaux
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    page: 0,
    limit: 10,
  });
  const [showFilters, setShowFilters] = useState(false);

  // Charger les compagnies
  useEffect(() => {
    const queryParams = {
      search: filters.search,
      status: filters.status,
      page: filters.page + 1, // API pagination souvent basée sur 1
      limit: filters.limit,
    };
    
    dispatch(fetchCompanies(queryParams));
  }, [dispatch, filters]);

  // Handlers
  const handleOpenDialog = (company = null) => {
    setCurrentCompany(company);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentCompany(null);
  };

  const handleOpenDeleteDialog = (company) => {
    setCurrentCompany(company);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setCurrentCompany(null);
  };

  const handleSaveCompany = (companyData) => {
    if (currentCompany) {
      // Mise à jour
      dispatch(updateCompany({ id: currentCompany.id, ...companyData }))
        .unwrap()
        .then(() => {
          setSnackbar({
            open: true,
            message: 'Compagnie mise à jour avec succès',
            severity: 'success',
          });
          handleCloseDialog();
        })
        .catch((error) => {
          setSnackbar({
            open: true,
            message: `Erreur: ${error}`,
            severity: 'error',
          });
        });
    } else {
      // Création
      dispatch(createCompany(companyData))
        .unwrap()
        .then(() => {
          setSnackbar({
            open: true,
            message: 'Compagnie créée avec succès',
            severity: 'success',
          });
          handleCloseDialog();
        })
        .catch((error) => {
          setSnackbar({
            open: true,
            message: `Erreur: ${error}`,
            severity: 'error',
          });
        });
    }
  };

  const handleDeleteCompany = () => {
    if (currentCompany) {
      dispatch(deleteCompany(currentCompany.id))
        .unwrap()
        .then(() => {
          setSnackbar({
            open: true,
            message: 'Compagnie supprimée avec succès',
            severity: 'success',
          });
          handleCloseDeleteDialog();
        })
        .catch((error) => {
          setSnackbar({
            open: true,
            message: `Erreur: ${error}`,
            severity: 'error',
          });
        });
    }
  };

  const handleToggleStatus = (company) => {
    dispatch(toggleCompanyStatus(company.id))
      .unwrap()
      .then(() => {
        setSnackbar({
          open: true,
          message: `Compagnie ${company.isActive ? 'désactivée' : 'activée'} avec succès`,
          severity: 'success',
        });
      })
      .catch((error) => {
        setSnackbar({
          open: true,
          message: `Erreur: ${error}`,
          severity: 'error',
        });
      });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleChangePage = (event, newPage) => {
    setFilters({ ...filters, page: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    setFilters({ ...filters, limit: parseInt(event.target.value, 10), page: 0 });
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters({ ...filters, [name]: value, page: 0 });
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      status: '',
      page: 0,
      limit: 10,
    });
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Gestion des Compagnies
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Ajouter une compagnie
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Barre de recherche et filtres */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: showFilters ? 2 : 0 }}>
          <TextField
            name="search"
            label="Rechercher une compagnie"
            variant="outlined"
            size="small"
            value={filters.search}
            onChange={handleFilterChange}
            sx={{ flexGrow: 1, mr: 2 }}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
              endAdornment: filters.search ? (
                <IconButton
                  size="small"
                  onClick={() => setFilters({ ...filters, search: '', page: 0 })}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              ) : null,
            }}
          />
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={toggleFilters}
            sx={{ mr: 1 }}
          >
            {showFilters ? 'Masquer les filtres' : 'Filtres'}
          </Button>
          {showFilters && (
            <Button variant="text" onClick={handleResetFilters}>
              Réinitialiser
            </Button>
          )}
        </Box>

        {showFilters && (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Statut</InputLabel>
                  <Select
                    name="status"
                    value={filters.status}
                    onChange={handleFilterChange}
                    label="Statut"
                  >
                    <MenuItem value="">Tous</MenuItem>
                    <MenuItem value="active">Actif</MenuItem>
                    <MenuItem value="inactive">Inactif</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Tableau des compagnies */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Compagnie</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Téléphone</TableCell>
              <TableCell>Licence</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Nombre de forfaits</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : companies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Aucune compagnie trouvée
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              companies.map((company) => (
                <TableRow key={company.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar
                        src={company.logo}
                        alt={company.name}
                        sx={{ width: 40, height: 40, mr: 2 }}
                      />
                      <Typography variant="body2" fontWeight="medium">
                        {company.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{company.email}</TableCell>
                  <TableCell>{company.phone}</TableCell>
                  <TableCell>{company.licenseNumber || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip
                      label={company.isActive ? 'Actif' : 'Inactif'}
                      color={company.isActive ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{company.packageCount || 0}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={company.isActive ? 'Désactiver' : 'Activer'}>
                      <IconButton
                        size="small"
                        color={company.isActive ? 'error' : 'success'}
                        onClick={() => handleToggleStatus(company)}
                      >
                        {company.isActive ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Modifier">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenDialog(company)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleOpenDeleteDialog(company)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={meta.totalItems || 0}
          rowsPerPage={filters.limit}
          page={filters.page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Lignes par page:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
        />
      </TableContainer>

      {/* Dialog pour ajouter/modifier une compagnie */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        <DialogTitle>
          {currentCompany ? 'Modifier la compagnie' : 'Ajouter une compagnie'}
        </DialogTitle>
        <DialogContent dividers>
          <CompanyForm
            initialData={currentCompany}
            onSave={handleSaveCompany}
            onCancel={handleCloseDialog}
          />
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmation de suppression */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirmation de suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer la compagnie "{currentCompany?.name}" ?
            Cette action est irréversible et supprimera également tous les forfaits associés.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Annuler</Button>
          <Button
            onClick={handleDeleteCompany}
            color="error"
            variant="contained"
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar pour les notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanyManagementPage;